/**
 * MultiSelectCheckbox — touch-friendly multi-select primitive.
 *
 * Phase 1.7.3 wrapper-class replacement for native `<select multiple>`.
 * Native multi-select requires Ctrl/Cmd-click for additional selection,
 * which does NOT work on touch devices. This component renders a
 * scrollable list of checkbox rows; clicking the row OR the checkbox
 * toggles selection. Tap targets are ≥44×44px per mobile-first acceptance.
 *
 * Used by SuggestResourceForm (service types / org specialties / counties /
 * individual specialties) and IndividualDetail (specialties picker).
 * Caller supplies `getOptionValue` + `getOptionLabel` so the same primitive
 * serves slug-based selection (lookup-row keys) and display-name-based
 * selection (free-text serialize) without per-call shape adapters.
 *
 * Checked entries first, and a filter box once the list is long (the
 * facet-list rules in src/lib/facetOrder.ts, shared with the Directory
 * filter sheets): checked rows sit at the top in the list's own order,
 * taken when the list mounts and on each change to the filter text, never
 * on a click, so a ticked row stays under the pointer. A list of more than
 * ten entries shows a filter in the list's own header: the house
 * SearchField, named `Filter {ariaLabel}`, with a "Clear filter" button
 * once there is text. It matches the label, case-insensitive, and the
 * checked rows stay visible above the matches. `filterPlaceholder` sets
 * the box's placeholder (default `Filter {ariaLabel, lower case}`); it no
 * longer decides whether the box shows. The filter state is the
 * primitive's own: a remount (a sheet closing) clears it.
 */

import { useState } from 'react';
import { SearchField } from '@/components/primitives/SearchField';
import { needsFacetFilter, orderFacetEntries, usePinnedSelection } from '@/lib/facetOrder';

export interface MultiSelectCheckboxProps<T> {
  options: T[];
  getOptionValue: (option: T) => string;
  getOptionLabel: (option: T) => string;
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  loading?: boolean;
  loadingHint?: string;
  emptyState?: string;
  ariaLabel?: string;
  /** The filter box's placeholder; the box itself shows once the list has more than ten entries. */
  filterPlaceholder?: string;
}

export function MultiSelectCheckbox<T>({
  options,
  getOptionValue,
  getOptionLabel,
  selectedValues,
  onChange,
  loading = false,
  loadingHint = 'Loading…',
  emptyState = 'No options available',
  ariaLabel,
  filterPlaceholder,
}: MultiSelectCheckboxProps<T>) {
  const [filterQuery, setFilterQuery] = useState('');
  const { pinned, markOwn } = usePinnedSelection(selectedValues, filterQuery);

  if (loading) {
    return (
      <div className="text-sm text-ink-soft italic px-2 py-2">{loadingHint}</div>
    );
  }
  if (options.length === 0) {
    return (
      <div className="text-sm text-ink-soft italic px-2 py-2">{emptyState}</div>
    );
  }

  const toggle = (value: string) => {
    const next = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    markOwn(next);
    onChange(next);
  };

  const filterEnabled = needsFacetFilter(options.length);
  const visibleOptions = orderFacetEntries(
    options,
    getOptionValue,
    getOptionLabel,
    pinned,
    filterEnabled ? filterQuery : '',
  );
  const listLabel = ariaLabel ?? 'options';

  return (
    <div className="overflow-hidden rounded border border-sage-300 bg-white">
      {filterEnabled && (
        <div className="border-b border-sage-800/[8%] p-2">
          <SearchField
            value={filterQuery}
            onChange={setFilterQuery}
            placeholder={filterPlaceholder ?? `Filter ${listLabel.toLowerCase()}`}
            aria-label={`Filter ${listLabel}`}
            clearLabel="Clear filter"
          />
        </div>
      )}
      <ul
        role="listbox"
        aria-multiselectable="true"
        aria-label={ariaLabel}
        className="max-h-48 overflow-y-auto divide-y divide-sage-800/[8%]"
      >
        {visibleOptions.length === 0 ? (
          <li className="text-sm text-ink-soft italic px-3 py-2" role="option" aria-selected="false">
            No matches
          </li>
        ) : (
          visibleOptions.map((option) => {
            const value = getOptionValue(option);
            const label = getOptionLabel(option);
            const checked = selectedValues.includes(value);
            return (
              <li key={value} role="option" aria-selected={checked}>
                <label className="flex items-center gap-2 px-3 py-2 min-h-[44px] cursor-pointer hover:bg-sage-50">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(value)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-ink">{label}</span>
                </label>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
}
